import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import Button from 'react-bootstrap/lib/Button';

import withRouter from 'react-router/lib/withRouter';
import { routerShape } from 'react-router/lib/PropTypes';

import t from '../../lib/I18n';

import Login from '../../records/customer/Login';
import User from '../../records/User';
import UserStore from '../../stores/UserStore';
import connectToStores from '../../helpers/connectToStores';
import {
    CustomerMaxFailedLoginAttemptsReached,
    CustomerAccountDormant,
    CustomerAccountLocked,
} from '../../errors/Customer';

import CustomerActions from '../../actions/CustomerActions';
import ModalActions from '../../actions/ModalActions';
import { GlobalModals } from '../../constants/AppConstants';
import { displayNotifications } from '../../helpers/NotificationCentre';

import FormWrapper from '../form/FormWrapper';
import ValidatedField from '../form/ValidatedField';
import DefaultLoginLayout from './DefaultLoginLayout';
import CustomerPassword from '../customer/Password';
import AccountLockedModal from './AccountLockedModal';
import PromptPasswordChangeModal from '../customer/PromptPasswordChangeModal';
import { checkIfChildSmartCardWillExpireSoon } from '../../utils/childSmartCardExpiration';

const getStateFromStores = () => ({
    user: UserStore.getCurrent()
});

@withRouter
export class LoginForm extends React.PureComponent {
    static propTypes = {
        user: PropTypes.instanceOf(User).isRequired,
        withSubmitButton: PropTypes.bool,
        onLogin: PropTypes.func,
        children: PropTypes.func,
        registerTitleText: PropTypes.string,
        titleText: PropTypes.string,
        onCancel: PropTypes.func,
        onCancelRegister: PropTypes.func,
        onRegisterSuccessClose: PropTypes.func,
        onFormChange: PropTypes.func,
        forceSmartcardSignup: PropTypes.bool,
        onCloseSmartcardError: PropTypes.func,
        disableSuccessDropdown: PropTypes.bool,
        onUserFieldBlur: PropTypes.func,
        onUserFieldChange: PropTypes.func,
        onSmartcardSuccess: PropTypes.func,
        router: routerShape.isRequired,
        hideSmartcardCheckbox: PropTypes.bool,
        hideSmartcardPromotionalText: PropTypes.bool,
        withISRN: PropTypes.bool,
        withoutNotifications: PropTypes.bool,
        emailDefaultValue: PropTypes.string,
        isBrochureRedirect: PropTypes.bool,
        initialPath: PropTypes.string,
    };

    static defaultProps = {
        withSubmitButton: false,
        children: DefaultLoginLayout,
        onLogin: _.noop,
        onRegisterSuccessClose: _.noop,
        forceSmartcardSignup: false,
        disableSuccessDropdown: false,
        onFormChange: _.noop,
        onUserFieldBlur: _.noop,
        onUserFieldChange: _.noop,
        hideSmartcardCheckbox: false,
        hideSmartcardPromotionalText: false,
        withISRN: false,
        onSmartcardSuccess: _.noop,
        onCloseSmartcardError: _.noop,
        withoutNotifications: false,
        emailDefaultValue: null,
        isBrochureRedirect: false,
    };

    state = {
        loginForm: new Login({
            username: this.props.emailDefaultValue,
        }),
        submitted: false,
        isLoggingIn: false,
        error: null
    };

    componentDidMount() {
        this._isMounted = true;
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    safeSetState(...args) {
        if (this._isMounted) {
            this.setState(...args);
        }
    }

    handleFieldChange = (field, value) => {
        if (field === 'username') {
            this.props.onUserFieldChange();
        }

        this.safeSetState({
                loginForm: this.state.loginForm.set(field, value),
                submitted: false,
                error: null
            },
            this.props.onFormChange(this.state.loginForm)
        );


    };

    handleForgotPassword = () => {
        ModalActions.open(GlobalModals.FORGOT_PASSWORD, {
            isBrochureRedirect: this.props.isBrochureRedirect
        });
    };

    handleRegister = () => {
        const {
            registerTitleText,
            titleText,
            onCancel,
            onCancelRegister,
            onRegisterSuccessClose,
            forceSmartcardSignup,
            disableSuccessDropdown,
            onSmartcardSuccess,
            hideSmartcardCheckbox,
            hideSmartcardPromotionalText,
            withISRN,
            onCloseSmartcardError
        } = this.props;

        ModalActions.open(
            GlobalModals.REGISTER,
            {
                    titleText: registerTitleText,
                    loginTitleText: titleText,
                    onCancel: onCancelRegister,
                    onCancelLogin: onCancel,
                    onSuccessClose: onRegisterSuccessClose,
                    forceSmartcardSignup,
                    disableSuccessDropdown,
                    onSmartcardSuccess,
                    hideSmartcardCheckbox,
                    hideSmartcardPromotionalText,
                    withISRN,
                    onCloseSmartcardError,
                });
    };

    handleLogin = async () => {
        const { withoutNotifications, router, initialPath} = this.props;
        const { loginForm } = this.state;
        this.safeSetState({
            submitted: true,
            isLoggingIn: loginForm.isComplete()
        });
        if (!loginForm.isComplete()) {
            return;
        }

        try {
            const user = await CustomerActions.login(loginForm);
            this.props.onLogin(user);
            this.safeSetState({ loginForm: new Login(), submitted: false });

            if (user.smartcardRegistered) {
                const smartCard = await CustomerActions.getSmartcard(user);
                checkIfChildSmartCardWillExpireSoon(smartCard);
            }

            if (!withoutNotifications) {
                if (user.authToken.showPasswordChangeModal) {
                    _.defer(ModalActions.open, PromptPasswordChangeModal, { user });
                } else {
                    _.defer(displayNotifications);
                }
            }

            _.defer(CustomerActions.getVouchers, user);
            if (initialPath) {
                router.push(initialPath);
            }
        } catch (error) {
            console.error(error);
            this.safeSetState({ error });
            if (error instanceof CustomerMaxFailedLoginAttemptsReached) {
                ModalActions.open(AccountLockedModal, {email: loginForm.username});
            } else if (error instanceof CustomerAccountDormant) {
                ModalActions.open(AccountLockedModal, {email: loginForm.username, isDormant: true});
            } else if (error instanceof CustomerAccountLocked) {
                ModalActions.open(AccountLockedModal, {isLocked: true});
            }
        } finally {
            this.safeSetState({ isLoggingIn: false });
        }
    };

    makeInput = ({ field, label, onBlur, type }) => {
        const { loginForm, submitted, isLoggingIn } = this.state;
        return (
            <ValidatedField
                form={ loginForm }
                onChange={ this.handleFieldChange }
                type={ type }
                id={ field }
                field={ field }
                label={ label }
                placeholder={ label }
                labelClassName="sr-only"
                visited={ submitted }
                disabled={ isLoggingIn }
                onBlur={ onBlur }
                showRequired
                hideOptional
                outsideLabel
            />
        );
    };

    makeLinkButton = ({ onClick, label }) => (
        <Button className="like-link text-sm" onClick={ onClick }>
            {label}
        </Button>
    );

    render() {
        const { user, withSubmitButton, children: Layout, onUserFieldBlur } = this.props;
        const { loginForm, submitted, isLoggingIn, error } = this.state;

        const isLoggedIn = _.result(user, 'isFullyLoaded');
        if (isLoggedIn) {
            return null;
        }

        const userInput = this.makeInput({
            field: 'username',
            label: t('login.username'),
            onBlur: onUserFieldBlur,
            type: 'email'
        });

        const passwordInput = (<CustomerPassword
            field="password"
            form={ loginForm }
            onChange={ this.handleFieldChange }
            showPasswordIcon
            visited={ submitted }
            showRequired
            outsideLabel
        />);

        const forgotPasswordButton = this.makeLinkButton({
            onClick: this.handleForgotPassword,
            label: t('login.forgotPassword.link')
        });

        const props = {
            userInput,
            passwordInput,
            forgotPasswordButton,
            withSubmitButton,
            submitted,
            isLoggingIn,
            form: loginForm,
            error: error && t('alertmessage.login.failed'),
            onInputChange: this.handleFieldChange,
            onRegister: this.handleRegister,
            onForgotPassword: this.handleForgotPassword,
            onLogin: this.handleLogin
        };

        return (
            <FormWrapper submitted={ submitted }>
                <Layout
                    { ...props }
                    loginComponent={ <DefaultLoginLayout { ...props } /> }
                />
            </FormWrapper>
        );
    }
}

export default connectToStores([UserStore], getStateFromStores)(LoginForm);
